import { type PropsWithChildren, type ReactElement, type ReactNode, useMemo } from 'react'
import { Box } from '../../primitives/Box'
import { Stack } from '../../primitives/Stack'
import { Body1, Body2, Caption1, H6 } from '../../primitives/Typography'
import { type DataTestIdAttribute, type HTMLAttributesWithoutStyling } from '../../types'
import { spacing } from '../../theme/spacing'
import { ListItemCollapse } from './ListItemCollapse'
import {
  type ListItemTextTranslatableField,
  type ListItemTextTranslates,
  type ListItemTextTranslatesInput,
} from './types'

export type ListItemTextProps = PropsWithChildren<
  {
    /**
     * Toggles bold styling on the primary title for emphasis.
     */
    emphasis?: boolean

    /**
     * When your listItem needs more initial shown/hidden content, content itself is placed inside prop `more`
     */
    isOpen?: boolean

    /**
     * Content shown when prop `isOpen` is truthy else hidden
     */
    more?: ReactNode

    /**
     * Our primary title
     * By default we need the `string`, only in UX cases where they do design something like highlighting or making part bold then we can use the `ReactNode`
     */
    primary?: string | ReactNode

    /**
     * Supporting the title, can also be something like text with BadgeInline etc.
     */
    secondary?: string | ReactElement

    /**
     * We can show a grey sub heading on top of the primary title
     */
    subHeading?: string

    /**
     * Some designs are with one ore more Chips or Badges
     */
    subHeadingNode?: ReactElement

    /**
     * When secondary is not enough :)
     */
    tertiary?: string

    /**
     * No translation for follwing possible props
     * Sometimes you do not want the part(s) to be translatable by the browser/user
     */
    translates?: ListItemTextTranslatesInput
  } & {
    id?: string
  } & DataTestIdAttribute &
    HTMLAttributesWithoutStyling<HTMLDivElement>
>

export const ListItemText = ({
  children,
  emphasis,
  isOpen,
  more,
  primary,
  secondary,
  subHeading,
  subHeadingNode,
  tertiary,
  translates,
  ...rest
}: ListItemTextProps) => {
  const Component = emphasis ? H6 : Body1
  const hasLines = !!(subHeadingNode || subHeading || primary || secondary || tertiary)

  const noTranslate = useMemo<ListItemTextTranslates>(() => {
    if (!translates) {
      return {}
    }

    return (Object.keys(translates) as ListItemTextTranslatableField[]).reduce((acc, key) => {
      acc[key] = 'no'
      return acc
    }, {} as ListItemTextTranslates)
  }, [translates])

  return (
    <Box
      {...rest}
      className="DhlListItem-text"
      sx={{
        flex: '1 1 auto',
        minWidth: 0,
        // defensive to not let any content break the list itself
        wordBreak: 'break-word',

        ...(hasLines && { minHeight: spacing(5.5) }),

        '.DhlListItem--vertical &': {
          width: '100%',
          textAlign: 'center',
          pr: 0,
        },
      }}
    >
      {hasLines && (
        <Stack sx={{ gap: 0.25 }}>
          {subHeadingNode && (
            <Stack direction="row" sx={{ gap: 1, mb: 0.5 }}>
              {subHeadingNode}
            </Stack>
          )}
          {subHeading && (
            <Caption1 variant="tertiary" translate={noTranslate.subHeading}>
              {subHeading}
            </Caption1>
          )}
          {primary && (
            <Component variant="inherit" translate={noTranslate.primary} component={emphasis ? 'strong' : 'div'}>
              {primary}
            </Component>
          )}
          {secondary && (
            <Body2 variant="secondary" translate={noTranslate.secondary} component="div">
              {secondary}
            </Body2>
          )}
          {tertiary && (
            <Body2 variant="tertiary" translate={noTranslate.tertiary}>
              {tertiary}
            </Body2>
          )}
        </Stack>
      )}

      <ListItemCollapse more={more} isOpen={isOpen}>
        {children}
      </ListItemCollapse>
    </Box>
  )
}
